// CalendarView's prop surface + a couple of small helpers. Lifted out
// of the component file so the implementation stays under the
// 500-line cap. The interface is the calendar's main wire seam — every
// surface flag, controlled/uncontrolled pair, and slot API is
// declared here.

import type { ReactNode } from 'react';
import type {
  CalendarCustomView,
  CalendarEvent,
  CalendarEventLink,
  CalendarEventStatus,
  CalendarGroup,
  CalendarKindMap,
  CalendarLocale,
  ViewMode,
} from './types.js';
import type { CalendarMessages } from './i18n.js';

export interface CalendarViewProps extends CalendarLocale {
  readonly events: readonly CalendarEvent[];
  readonly kindTheme?: CalendarKindMap | undefined;
  readonly groups?: readonly CalendarGroup[] | undefined;

  // ---------- view ----------
  /** Initial view. On mobile, falls back to 'agenda' if not specified. */
  readonly defaultView?: ViewMode | string | undefined;
  /** Controlled view mode. Pair with `onViewChange`. */
  readonly view?: ViewMode | string | undefined;
  readonly onViewChange?: ((view: ViewMode | string) => void) | undefined;
  /** Restrict the toolbar's view options. Default ['month','week','day','agenda']. */
  readonly views?: readonly (ViewMode | string)[] | undefined;
  /** Consumer-defined views appended to the built-in set. */
  readonly customViews?: readonly CalendarCustomView[] | undefined;

  // ---------- date state ----------
  /** Defaults to viewer-local "today" projected via `viewerTz`. */
  readonly today?: string | undefined;
  readonly cursor?: string | undefined;
  readonly onCursorChange?: ((next: string) => void) | undefined;
  readonly defaultCursor?: string | undefined;
  readonly selectedDate?: string | undefined;
  readonly onSelectedDateChange?: ((next: string) => void) | undefined;
  readonly defaultSelectedDate?: string | undefined;

  // ---------- filters ----------
  readonly kindFilter?: ReadonlySet<string> | undefined;
  readonly onKindFilterChange?: ((next: Set<string>) => void) | undefined;
  readonly groupFilter?: ReadonlySet<string> | undefined;
  readonly onGroupFilterChange?: ((next: Set<string>) => void) | undefined;
  readonly statusFilter?: ReadonlySet<CalendarEventStatus> | undefined;
  readonly onStatusFilterChange?: ((next: Set<CalendarEventStatus>) => void) | undefined;

  // ---------- callbacks ----------
  readonly onSelectEvent?: ((event: CalendarEvent) => void) | undefined;
  readonly onSelectLink?: ((link: CalendarEventLink, event: CalendarEvent) => void) | undefined;
  /** Fired by the "New event" toolbar button / FAB AND by drag-to-create
   *  on the timed grid. The optional `draft` carries seed values when
   *  available — `endsAt` is filled by drag-to-create; the mobile FAB
   *  and "add on this day" entry-points pass only `startsAt` (a
   *  date-only ISO of the day in view), so the composer can prefill
   *  the date without forcing a duration on the user. */
  readonly onCreateEvent?: (
    draft?: { readonly startsAt: string; readonly endsAt?: string },
  ) => void;
  /** Fired when the user drag-reschedules or drag-resizes an event. The
   *  patch contains the resolved new times — caller is responsible for
   *  the actual update (e.g. PATCH /v1/calendar/{id}). */
  readonly onUpdateEvent?: (
    id: string,
    patch: { readonly startsAt: string; readonly endsAt: string | null },
  ) => void;
  /** Fired by the right-click "Cancel event" item (toggles `status`). */
  readonly onCancelEvent?: ((event: CalendarEvent) => void) | undefined;
  /** Fired by the right-click "Delete" item. */
  readonly onDeleteEvent?: ((event: CalendarEvent) => void) | undefined;
  /** Fired by the right-click "Duplicate" item. */
  readonly onDuplicateEvent?: ((event: CalendarEvent) => void) | undefined;
  /** Fired by the right-click "Edit…" item. Defaults to `onSelectEvent`
   *  when omitted, so the consumer's existing detail flow opens for edit. */
  readonly onEditEvent?: ((event: CalendarEvent) => void) | undefined;
  /** Per-event action permissions. Missing booleans default to `true`
   *  so existing consumers keep their previous behaviour. */
  readonly eventActionPermissions?:
    | ((event: CalendarEvent) => CalendarEventActionPermissions)
    | undefined;

  // ---------- chrome ----------
  readonly title?: string | undefined;
  readonly className?: string | undefined;
  /** Render the built-in toolbar title block. Full-page consumers can
   *  set this false when a site-level PageHeader already owns the H1. */
  readonly showToolbarTitle?: boolean | undefined;
  /** Render the desktop "New event" button inside the calendar toolbar.
   *  Drag-create, mobile FAB, and composer wiring still use
   *  `onCreateEvent`; this only controls the toolbar CTA. */
  readonly showToolbarCreateButton?: boolean | undefined;
  /** Extra content rendered to the right of the view switcher inside
   *  the toolbar (e.g. domain-specific shortcut buttons). */
  readonly toolbarExtras?: ReactNode | undefined;
  /** A consumer-rendered composer modal/drawer (e.g. EventComposer).
   *  Triggered via `onCreateEvent`. The package only owns the trigger
   *  surface; the modal lives outside. */
  readonly composer?: ReactNode | undefined;
  /** When true, render an "Export .ics" button in the toolbar. Default
   *  true — set false to hide if the consumer has its own. */
  readonly enableICSExport?: boolean | undefined;

  // ---------- slot APIs ----------
  /** Inject domain content (e.g. AvailabilityForDate booking cards) below
   *  the day's event list in Month/Day rails. */
  readonly renderDayRailExtras?: ((dateKey: string) => ReactNode) | undefined;
  /** Inject domain content (e.g. AttendanceRoster) inside each EventCard. */
  readonly renderEventExtras?: ((event: CalendarEvent) => ReactNode) | undefined;

  /** Localised strings for every label the calendar renders. Each key
   *  is optional; missing keys fall back to English defaults. The
   *  package wires the merged object into a React context so deep
   *  child components (FilterBar, EventDetailSheet, …) read translations
   *  without prop drilling. */
  readonly messages?: Partial<CalendarMessages> | undefined;
}

export const BUILT_IN_VIEWS: readonly ViewMode[] = ['month', 'week', 'day', 'agenda'];

export interface CalendarEventActionPermissions {
  readonly canOpen?: boolean | undefined;
  readonly canEdit?: boolean | undefined;
  readonly canDuplicate?: boolean | undefined;
  readonly canDelete?: boolean | undefined;
  readonly canUpdate?: boolean | undefined;
}

export function viewLabel(messages: CalendarMessages, mode: ViewMode): string {
  switch (mode) {
    case 'month':
      return messages.viewMonth;
    case 'week':
      return messages.viewWeek;
    case 'day':
      return messages.viewDay;
    case 'agenda':
      return messages.viewAgenda;
  }
}

/**
 * Strip filesystem-illegal characters out of the ICS filename stem.
 * Reserved by Windows / macOS / Linux paths, plus control characters
 * and trailing dots that Windows silently drops. Empty stems collapse
 * to `'calendar'` so the user always gets a usable file.
 */
export function icsFilenameStem(title: string): string {
  const stem = title
    .toLowerCase()
    .replace(/\s+/g, '-')
    // eslint-disable-next-line no-control-regex
    .replace(/[\\/:*?"<>|\x00-\x1f]/g, '')
    .replace(/\.+$/, '')
    .slice(0, 100);
  return stem.length > 0 ? stem : 'calendar';
}
